import { useState } from "react";
import { Button, Field, Input, Modal, Switch } from "../../ds";

// Ein Formular für jede Liste, die aus benannten uci-Sections besteht: welche
// Felder es zeigt, steht in der Spec daneben (firewallSpecs.tsx, mwanSpecs.tsx).
// Acht handgeschriebene Dialoge wären achtmal dieselbe Arbeit — und sieben
// Gelegenheiten, einen davon zu vergessen.

/** Ein Eintrag einer Auswahl. Als bloßer String, wo der Wert für sich spricht
 *  (`tcp`), als Paar, wo er es nicht tut (`10.0.0.5` · `wallbox-hof`). */
export type FieldOption = string | { value: string; label: string };

export interface FieldSpec {
  key: string;
  label: string;
  kind: "text" | "number" | "bool" | "select" | "list" | "combo";
  /** `select`: die ganze Auswahl. `combo`: die Vorschläge neben der freien
   *  Eingabe. */
  options?: FieldOption[];
  /** Beschriftung des Leer-Eintrags — nur dort setzen, wo „nichts“ eine
   *  Antwort ist („leer = der Router selbst“). */
  emptyLabel?: string;
  help?: string;
  /** Param-Name auf dem Draht, wenn er vom Feld abweicht (mwan3 schreibt
   *  snake_case, gelesen wird camelCase). */
  wire?: string;
}

export type FieldValue = string | number | boolean | string[] | null;

interface Option {
  value: string;
  label: string;
}

const asOption = (option: FieldOption): Option =>
  typeof option === "string" ? { value: option, label: option } : option;

const asText = (value: FieldValue): string => (typeof value === "string" ? value : "");

function readField(entry: object, spec: FieldSpec): FieldValue {
  const raw = (entry as Record<string, unknown>)[spec.key];
  if (spec.kind === "bool") return raw === true;
  if (spec.kind === "list") return Array.isArray(raw) ? (raw as string[]) : [];
  if (spec.kind === "number") return typeof raw === "number" ? raw : "";
  return typeof raw === "string" ? raw : "";
}

/** Sentinel: „nicht aus der Liste, sondern getippt“. */
const CUSTOM = "__frei__";

/** Auswahl aus den bekannten Geräten ODER eine freie Adresse — dieselbe
 *  Combobox wie in LuCI. ARP/DHCP deckt den Normalfall ab; getippt wird nur,
 *  was der Router noch nie gesehen hat (Gerät aus, feste Adresse geplant). */
function ComboInput({
  spec,
  value,
  onChange,
}: {
  spec: FieldSpec;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
}) {
  // Gemerkt wird nur die ausdrückliche Entscheidung „freie Eingabe“. Ob der
  // Wert in der Liste steht, entscheidet sonst die Liste selbst — sie trifft
  // erst nach dem Öffnen ein.
  const [typed, setTyped] = useState(false);
  const current = asText(value);
  const options = (spec.options ?? []).map(asOption);
  const free = typed || (current !== "" && !options.some((o) => o.value === current));
  const id = `entry-${spec.key}`;

  return (
    <Field label={spec.label} helperText={spec.help} htmlFor={id}>
      <select
        id={id}
        className="ds-input"
        value={free ? CUSTOM : current}
        onChange={(e) => {
          const picked = e.target.value;
          setTyped(picked === CUSTOM);
          // Beim Wechsel auf freie Eingabe steht das Feld leer da — der
          // vorherige Wert war ein Gerät, kein Tippfehler-Anfang.
          onChange(picked === CUSTOM || picked === "" ? null : picked);
        }}
      >
        <option value="">{spec.emptyLabel ?? "— keins —"}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
        <option value={CUSTOM}>Andere Adresse …</option>
      </select>
      {free && (
        <input
          className="ds-input mt-2"
          value={current}
          placeholder="192.168.1.50"
          autoFocus
          aria-label={`${spec.label} — freie Eingabe`}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        />
      )}
    </Field>
  );
}

function FieldInput({
  spec,
  value,
  onChange,
}: {
  spec: FieldSpec;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
}) {
  if (spec.kind === "bool") {
    return <Switch label={spec.label} checked={value === true} onChange={onChange} />;
  }
  if (spec.kind === "select") {
    const current = asText(value);
    const options = (spec.options ?? []).map(asOption);
    // Ein Wert, den die Auswahl nicht kennt — eine Zone, die im Entwurf nicht
    // (mehr) steht —, darf nicht stillschweigend zum ersten Eintrag werden.
    if (current && !options.some((o) => o.value === current))
      options.unshift({ value: current, label: current });
    return (
      <Field label={spec.label} helperText={spec.help} htmlFor={`entry-${spec.key}`}>
        <select
          id={`entry-${spec.key}`}
          className="ds-input"
          value={current}
          // Leere Auswahl heißt in uci „Option weg", nicht „leerer String".
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        >
          {(spec.emptyLabel !== undefined || current === "") && (
            <option value="">{spec.emptyLabel ?? "—"}</option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>
    );
  }
  if (spec.kind === "combo") {
    return <ComboInput spec={spec} value={value} onChange={onChange} />;
  }
  if (spec.kind === "list") {
    return (
      <Input
        label={spec.label}
        helperText={spec.help}
        value={Array.isArray(value) ? value.join(", ") : ""}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
          )
        }
      />
    );
  }
  if (spec.kind === "number") {
    return (
      <Input
        label={spec.label}
        helperText={spec.help}
        inputMode="numeric"
        value={value === null || value === "" ? "" : String(value)}
        // Leeres Zahlenfeld heißt „Option weg", nicht „0".
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    );
  }
  return (
    <Input
      label={spec.label}
      helperText={spec.help}
      value={typeof value === "string" ? value : ""}
      // Leeres Textfeld heißt in uci „Option weg", nicht „leerer String".
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
    />
  );
}

export function EntryModal<T extends object>({
  fields,
  singular,
  entry,
  isNew,
  subtitle = "Die Änderung geht erst mit „Anwenden“ zum Router.",
  onChange,
  onSave,
  onClose,
}: {
  fields: FieldSpec[] | null;
  singular: string;
  entry: T | null;
  isNew: boolean;
  subtitle?: string;
  onChange: (entry: T) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  if (fields === null || entry === null) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${singular} ${isNew ? "anlegen" : "bearbeiten"}`}
      subtitle={subtitle}
      size="lg"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" iconName="check" onClick={onSave}>
            Übernehmen
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {fields.map((field) => (
          <FieldInput
            key={field.key}
            spec={field}
            value={readField(entry, field)}
            onChange={(value) => onChange({ ...entry, [field.key]: value })}
          />
        ))}
      </div>
    </Modal>
  );
}
